import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import {
  Box,
  alpha,
  Stack,
  lighten,
  Divider,
  styled,
  useTheme,
  Typography,
  Link,
} from '@mui/material'
import AppleIcon from '@mui/icons-material/Apple'

import HeaderUserbox from './Userbox'
import { useAppSelector, useAppDispatch } from '../../../hooks'
import { getUserProfileDataAction } from '../../../store/users'
import { getSelectedStoreId, setUserId, setUserStoreId } from '../../../utils'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useFeatureAnnouncements } from '../../../contexts/FeatureAnnouncementContext'
import { createCommandPaletteAnnouncement } from '../../../utils/featureAnnouncements'

// Detect OS and return appropriate keyboard shortcut hint
const getKeyboardShortcut = (): string => {
  if (typeof window === 'undefined') return 'Ctrl+K'
  
  const platform = navigator.platform.toLowerCase()
  const userAgent = navigator.userAgent.toLowerCase()
  
  // Check for Mac
  if (platform.includes('mac') || userAgent.includes('mac')) {
    return 'Cmd+K'
  }
  
  // Default to Windows/Linux (Ctrl+K)
  return 'Ctrl+K'
}

// Get user type label
const getUserTypeLabel = (data: any): string | null => {
  if (!data || !data.store) return null
  
  const selectedStoreId = getSelectedStoreId()
  const selectedStoreIdNum = selectedStoreId ? parseInt(selectedStoreId, 10) : 0
  const userStoreId = data.store.id || data.store.storeId || 0
  
  // If selectedStoreId is 0 or matches user's store.id, user is OWNER
  if (selectedStoreIdNum === 0 || selectedStoreIdNum === userStoreId) {
    return 'OWNER'
  }
  
  // Otherwise, it's STAFF - find the store from myStores array
  if (data.myStores && Array.isArray(data.myStores) && data.myStores.length > 0) {
    const selectedStore = data.myStores.find(
      (store: any) => (store.id === selectedStoreIdNum || store.storeId === selectedStoreIdNum)
    )
    
    if (selectedStore && selectedStore.userLevel) {
      const userLevel = selectedStore.userLevel.toUpperCase()
      return `STAFF(${userLevel})`
    }
    
    // If userLevel not found, default to STAFF(ADMIN)
    return 'STAFF(ADMIN)'
  }
  
  // Fallback
  return 'STAFF(ADMIN)'
}


const HeaderWrapper = styled(Box)(
  ({ theme }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 2)};
        right: 0;
        z-index: 6;
        background-color: ${alpha(theme.header.background, 0.95)};
        backdrop-filter: blur(3px);
        position: fixed;
        justify-content: flex-end;
        left: ${theme.sidebar.width};
        width: auto;
`
)

function Header() {
  const theme = useTheme()
  const dispatchAction = useAppDispatch()
  const { data, selectedStore } = useAppSelector((state) => state.userData)
  const location = useLocation()
  const isOnboarding = location.pathname.includes('/onboarding')
  const { addAnnouncement } = useFeatureAnnouncements()

  useEffect(() => {
    if (!data) {
      dispatchAction(getUserProfileDataAction())
    }
  }, [data, dispatchAction])

  // Persist userId/storeId to localStorage when profile loads (e.g. after reload) so analytics has fallback
  useEffect(() => {
    if (data) {
      setUserStoreId(data.store.id)
      setUserId(data.id)
      AnalyticsManager.shared.updateUserProperties()
    }
  }, [data])

  useEffect(() => {
    console.log(selectedStore)
    if (selectedStore) {
      dispatchAction(getUserProfileDataAction())
    }
  }, [selectedStore])

  // Show command palette announcement for logged-in users (not on onboarding)
  useEffect(() => {
    if (data && !isOnboarding) {
      // Check if already dismissed
      const isDismissed = typeof window !== 'undefined' && 
        localStorage.getItem('feature_announcement_command-palette-intro') === 'dismissed'
      
      // Only add if not dismissed
      if (!isDismissed) {
        const announcement = createCommandPaletteAnnouncement()
        addAnnouncement(announcement)
      }
    }
  }, [data, isOnboarding, addAnnouncement])

  return (
    <HeaderWrapper
      display="flex"
      alignItems="center"
      sx={{
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 1px 0 ${alpha(
                lighten(theme.colors.primary.main, 0.7),
                0.15
              )}, 0px 1px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px -1px rgba(0, 0, 0, .1)`
            : `0px 1.5px 1px -1px ${alpha(
                theme.colors.alpha.black[100],
                0.2
              )}, 0px 1px 1px -1px ${alpha(
                theme.colors.alpha.black[100],
                0.1
              )}`,
      }}
    >
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between"
        width="100%"
      >
        {/* Left side - User Type Tag and Keyboard Shortcut Hint */}
        {data  && (
          <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
            {/* User Type Tag */}
            {getUserTypeLabel(data) && !isOnboarding && (
              <Box
                sx={{
                  backgroundColor: getUserTypeLabel(data) === 'OWNER' ? '#28a745' : '#dc3545',
                  color: 'white',
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {getUserTypeLabel(data)}
              </Box>
            )}
            {/* Keyboard Shortcut Hint */}
            { !isOnboarding && (<Typography
              sx={{
                fontSize: '0.9rem',
                color: '#009900',
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Use <Box 
                component="span" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'primary.main',
                }}
              >
                {getKeyboardShortcut()}
              </Box> to select sidebar menu
            </Typography>)}
          </Box>
        )}

        {/* Right side - App Store Icons, WhatsApp and User */}
        <Box display="flex" alignItems="center" gap={1}>
          {/* Play Store Icon */}
          

          {/* App Store Icon */}
         

          {data && (
            <>
              <WhatsAppIcon sx={{ color: 'green', ml: 1 }} />
              <Link
                sx={{ fontSize: 15, mr: 3 }}
                href="https://web.whatsapp.com/send?phone=917976757979"
                color={'green'}
              >
                {' '}
                +917976757979
              </Link>
            </>
          )}

          <HeaderUserbox data={data} />
        </Box>
      </Box>
    </HeaderWrapper>
  )
}

export default Header
